import type { RewardOption, RewardState } from '../../components/v3/RewardCard'
import type { LinkedCustomer } from '../../screens/v3/CustomerScreen'
import { withBirthdayFirst } from '../../screens/v3/birthday'
import type { V2CustomerResult, V2Reward } from './types'

/** Turn what the surface returns into what the v3 screens take.
 *
 * A seam on purpose. The server's shape follows the spec's §12 projections and the
 * screens' shape follows the design; neither should have to move because the other did,
 * and putting the mapping in one tested function means a field that disappears breaks
 * here rather than three screens deep.
 */

/** Which of the four badge states a reward is in.
 *
 * Order matters. `unavailable` wins over everything because it is a property of the
 * LOCATION, not the reward: when the shop cannot serve loyalty at all, "you can afford
 * this" is not a true thing to say about any reward.
 */
export function rewardStateOf(
  reward: V2Reward,
  { locationAvailable, appliedRewardIds }: {
    locationAvailable: boolean
    appliedRewardIds?: ReadonlySet<string>
  },
): RewardState {
  if (!locationAvailable) return 'unavailable'
  if (appliedRewardIds?.has(reward.reward_id)) return 'applied'
  // `redeemable` is the server's answer and is stricter than `affordable`: it also
  // accounts for location scope and reward status. Trusting `affordable` alone would
  // offer a reward the server would then refuse.
  if (!reward.redeemable) return 'unaffordable'
  return 'use'
}

export function toRewardOptions(
  result: V2CustomerResult,
  options: {
    locationAvailable: boolean
    appliedRewardIds?: ReadonlySet<string>
    birthdayRewardIds?: ReadonlySet<string>
  },
): RewardOption[] {
  const rewards = result.rewards.map((reward) => ({
    id: reward.reward_id,
    name: reward.name,
    pointsCost: reward.points_cost,
    imageUrl: reward.image_url,
    state: rewardStateOf(reward, options),
    birthday: options.birthdayRewardIds?.has(reward.reward_id) ?? false,
  }))
  // The designer's rule: a birthday reward is shown first. Everything else keeps the
  // server's order, which is the merchant's display order.
  return withBirthdayFirst(rewards)
}

export function toLinkedCustomer(
  result: V2CustomerResult,
  { name, pointsAfterPayment }: { name: string; pointsAfterPayment?: number | null },
): LinkedCustomer {
  const wallet = result.wallet
  return {
    name,
    // AVAILABLE, not settled. `balance` includes points already held by an open claim
    // on this very sale, so showing it would tell the customer they can spend money
    // that is already committed.
    points: wallet?.available_balance ?? 0,
    pointsAfterPayment,
    visits: wallet?.qualifying_visits ?? null,
    // A customer with no wallet at this business has never earned here. That is a real
    // state, not missing data, and the screen renders it as absent rather than zero.
    clientSince: null,
    rewardsClaimed: null,
    needsSignUp: wallet === null,
  }
}
